import { COOKIES, NBN_SESSION_KEYS, SESSION_KEYS } from '@belong/constants';
import { clearAuthCookie, clearCookie, clearMfaEnrolCookie } from '@belong/cookies';
import { isBrowser } from '@belong/env';
import { logger } from '@belong/logging';
import { clearKeyFromSessionStorage } from '@belong/session-storage';
import type { IAuthenticators, ObjectValues } from '@belong/types';
import { ELEVATED_SCOPES, OOB_CHANNELS, READ_PROFILE_SCOPES } from '@belong/types';
import { postAuthenticationMessage } from '../useAuthenticationListener';

const checkForScopes = (scope: string, scopes: string[]): boolean => {
  return scopes.some(s => scope.includes(s));
};

export const isUserSteppedUp = (scope: string): boolean => {
  return checkForScopes(scope, ELEVATED_SCOPES);
};

export const canUserReadProfile = (scope: string): boolean => {
  return checkForScopes(scope, READ_PROFILE_SCOPES);
};

export const isUserMfaEnrolled = (authenticators: IAuthenticators): boolean => {
  return authenticators.some(
    authenticator =>
      authenticator.active &&
      (authenticator.oobChannel === OOB_CHANNELS.SMS || authenticator.oobChannel === OOB_CHANNELS.EMAIL)
  );
};

export const clearSession = (): void => {
  clearAuthCookie();
  clearMfaEnrolCookie();
  clearNbnStorage();

  clearCookie(COOKIES.UNIQUE_IDENTIFIER);
  clearCookie(COOKIES.SESSION);
  clearCookie(COOKIES.IDV_SESSION);

  clearKeyFromSessionStorage(SESSION_KEYS.NOTIFICATION_CHANNEL);
  clearKeyFromSessionStorage(SESSION_KEYS.SELECTED_PLAN_CODE);
  clearKeyFromSessionStorage(SESSION_KEYS.USER_SESSION);
};

export const logout = (redirectTo: string): void => {
  clearSession();

  // notify any other tabs & windows that logout has occurred
  postAuthenticationMessage({ action: 'logout' });

  // force a page change/refresh to clear browser memory and mobx stores
  if (isBrowser()) {
    window.location.assign(redirectTo);
  }
};

export const clearServiceQueryData = (storage: Storage): void => {
  // Exclude these specific keys
  const excludeKeys: ObjectValues<typeof NBN_SESSION_KEYS>[] = [NBN_SESSION_KEYS.SQ_REFERRAL];

  Object.values(NBN_SESSION_KEYS)
    .filter(key => !excludeKeys.includes(key))
    .forEach(key => {
      storage.removeItem(key);
    });
};

export const clearAgentSession = (): void => {
  logger.info('Agent log out, clear agent session');
  clearSession();
};

export const clearNbnStorage = (): void => {
  Object.values(NBN_SESSION_KEYS).forEach(key => clearKeyFromSessionStorage(key));
  clearCookie(COOKIES.NBN_PROMO);
};
